import { clsx } from '@alexis/helpers/clsx';
import useFlightSearchPageParams from '@pages/FlightSearchResult/hooks/useFlightSearchPageParams';
import useFlightSearchSort from '@pages/FlightSearchResult/hooks/useFlightSearchSort';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import TickIcon from '@icons/tick_small.svg';

import { getIsRtlState, getTranslationsState } from '@redux/selectors';

import type { FlightSortType } from '@wegoTypes/flights/flightSortType';
import type { SortOrder } from '@wegoTypes/sortOrder';

import styles from './FlightSortDropdownTab.module.scss';

export type FlightSortDropdownTabProps = {
  handleOnSortTypeChanged(sortType: FlightSortType, sortOrder: SortOrder): void;
};

const FlightSortDropdownTab: React.FC<FlightSortDropdownTabProps> = ({
  handleOnSortTypeChanged,
}) => {
  const isRtl = useSelector(getIsRtlState);
  const translations = useSelector(getTranslationsState);

  const { isMultiCity, legs } = useFlightSearchPageParams();

  const { sortOrder: selectedSortOrder, sortType: selectedSortType } = useFlightSearchSort();

  const [isFlightTimeExpanded, setIsFlightTimeExpanded] = useState<boolean>(false);

  // Set all picker focus to false if body is clicked
  useEffect(() => {
    if (isFlightTimeExpanded) {
      const handleBodyOnClick = (): void => {
        if (isFlightTimeExpanded) {
          setIsFlightTimeExpanded(false);
        }
      };

      document.body.addEventListener('click', handleBodyOnClick);

      return () => {
        document.body.removeEventListener('click', handleBodyOnClick);
      };
    }
  }, [isFlightTimeExpanded]);

  const getLabelForFlightTimeSortOptions = useCallback(
    (isMultiCitySearch: boolean, legIndex: number, sortOrder: SortOrder) => {
      if (isMultiCitySearch) {
        return sortOrder === 'asc' ? translations.lbl_earliest : translations.lbl_latest;
      }

      if (legIndex === 0) {
        return sortOrder === 'asc'
          ? translations.departure_earliest
          : translations.departure_latest;
      }

      return sortOrder === 'asc' ? translations.return_earliest : translations.return_latest;
    },
    [
      translations.lbl_earliest,
      translations.lbl_latest,
      translations.departure_earliest,
      translations.departure_latest,
      translations.return_earliest,
      translations.return_latest,
    ],
  );

  return (
    <>
      <button
        className={clsx(
          styles.sortButton,
          /leg[1-6]_departure_time/.test(selectedSortType) && styles.active,
          isFlightTimeExpanded && styles.expanded,
          styles.time,
          isRtl && styles.rtl,
        )}
        onClick={() => setIsFlightTimeExpanded((prev) => !prev)}
      >
        <div className={styles.header}>
          <div className={clsx(styles.label, styles.withMultiLineEllipsis)}>
            {translations.sort_by_time}
          </div>
        </div>

        <i className={clsx(styles.chevron, styles.bottom)}></i>

        {/* Flight time dropdown */}
        {isFlightTimeExpanded ? (
          <div className={styles.dropdown} onClick={(e) => e.stopPropagation()}>
            {legs.map((leg, index) => {
              const arrivalCode = leg.arrivalAirportCode || leg.arrivalCityCode;
              const departureCode = leg.departureAirportCode || leg.departureCityCode;
              const codes = isRtl ? [arrivalCode, departureCode] : [departureCode, arrivalCode];
              const arrow = isRtl ? ' ← ' : ' → ';
              const legInformation = `${codes.join(arrow)} ${translations.filter_departure}`;
              const sortType = `leg${index + 1}_departure_time`;

              return (
                <React.Fragment key={index}>
                  {isMultiCity && (
                    <div className={styles.subHeader}>
                      <div className={styles.legNumber}>{index + 1}</div>
                      <div className={styles.legDetails}>{legInformation}</div>
                    </div>
                  )}
                  {['asc', 'desc'].map((sortOrder) => {
                    return (
                      <div
                        key={sortOrder}
                        className={clsx(
                          styles.item,
                          selectedSortType === sortType &&
                            selectedSortOrder === sortOrder &&
                            styles.active,
                        )}
                        onClick={() => {
                          setIsFlightTimeExpanded(false);
                          handleOnSortTypeChanged(
                            sortType as FlightSortType,
                            sortOrder as SortOrder,
                          );
                        }}
                      >
                        {getLabelForFlightTimeSortOptions(
                          isMultiCity,
                          index,
                          sortOrder as SortOrder,
                        )}

                        <TickIcon
                          className={clsx(
                            styles.tickIcon,
                            selectedSortType === sortType &&
                              selectedSortOrder === sortOrder &&
                              styles.show,
                          )}
                        />
                      </div>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </div>
        ) : null}
      </button>
    </>
  );
};

export default FlightSortDropdownTab;
